import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Copy, Trash2, PenLine } from "lucide-react";
import client from "@/lib/glossyApi";

const copyText = async (text, label = "Copied") => {
  try { await navigator.clipboard.writeText(text); toast.success(`${label} — paste it into an Instagram DM.`); }
  catch { toast.error("Copy failed."); }
};

const ScriptCard = ({ s, onDelete }) => (
  <div className="border border-ink/15 p-6" data-testid={`outreach-card-${s.id}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="overline text-gloss-silver">{[s.name, s.business].filter(Boolean).join(" · ")}</span>
      <button onClick={() => onDelete(s)} className="p-1.5 hover:text-red-500 transition-colors" aria-label="Delete" data-testid={`outreach-delete-${s.id}`}><Trash2 size={15} /></button>
    </div>
    <div className="space-y-3">
      {(s.openers || []).map((o, i) => (
        <div key={i} className="bg-secondary p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="font-sans text-sm leading-relaxed whitespace-pre-line flex-1">{o}</p>
            <button onClick={() => copyText(o, "DM copied")} className="shrink-0 p-1.5 hover:text-gloss-gold transition-colors" data-testid={`outreach-copy-${s.id}-${i}`}><Copy size={14} /></button>
          </div>
        </div>
      ))}
      {s.follow_up && (
        <div className="border border-ink/10 p-4">
          <p className="overline text-ink/50 mb-1">If they go quiet — follow-up</p>
          <div className="flex items-start justify-between gap-3">
            <p className="font-sans text-sm leading-relaxed flex-1">{s.follow_up}</p>
            <button onClick={() => copyText(s.follow_up, "Follow-up copied")} className="shrink-0 p-1.5 hover:text-gloss-gold transition-colors" data-testid={`outreach-copy-followup-${s.id}`}><Copy size={14} /></button>
          </div>
        </div>
      )}
    </div>
  </div>
);

export const Outreach = () => {
  const [scripts, setScripts] = useState([]);
  const [form, setForm] = useState({ name: "", business: "", industry: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => client.get("/admin/social/outreach").then((r) => setScripts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const generate = async () => {
    if (!form.name.trim() && !form.business.trim()) { toast.error("Give the desk at least a name or a business."); return; }
    setBusy(true);
    try { await client.post("/admin/social/outreach", form); setForm({ name: "", business: "", industry: "", notes: "" }); await load(); toast.success("Pitches written — pick your favourite and send it."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't write the pitch — try again."); }
    setBusy(false);
  };
  const remove = async (s) => { await client.delete(`/admin/social/outreach/${s.id}`); toast.success("Deleted."); load(); };

  return (
    <div>
      <div className="bg-ink text-cream p-6 sm:p-8 grain mb-10" data-testid="outreach-panel">
        <div className="relative z-10 space-y-4">
          <p className="overline text-gloss-gold">Outreach Scripts — win SME owners by DM</p>
          <p className="font-sans text-sm text-cream/60 max-w-2xl">Instagram doesn't allow automated cold DMs (accounts get banned), so this desk writes personal, editor-quality pitches for you to copy and send by hand — the safe way that actually converts.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.name} onChange={set("name")} placeholder="Owner's name, e.g. Sofia" className="bg-transparent border border-cream/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="outreach-name-input" />
            <input value={form.business} onChange={set("business")} placeholder="Business / @handle, e.g. @maisonverre" className="bg-transparent border border-cream/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="outreach-business-input" />
            <input value={form.industry} onChange={set("industry")} placeholder="Industry, e.g. florist in Cardiff" className="bg-transparent border border-cream/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="outreach-industry-input" />
          </div>
          <input value={form.notes} onChange={set("notes")} placeholder="What caught your eye about them? e.g. beautiful shopfront reel last week" className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="outreach-notes-input" />
          <button onClick={generate} disabled={busy} className="bg-cream text-ink px-6 py-3 overline flex items-center gap-2 hover:bg-gloss-gold transition-colors disabled:opacity-60" data-testid="outreach-generate-btn">
            {busy ? <RefreshCw size={16} className="animate-spin" /> : <PenLine size={16} />} {busy ? "Writing…" : "Write the pitch"}
          </button>
        </div>
      </div>
      <div className="space-y-5" data-testid="outreach-list">
        {scripts.map((s) => <ScriptCard key={s.id} s={s} onDelete={remove} />)}
        {scripts.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50">No pitches yet — tell the desk who you'd love in the magazine.</p>}
      </div>
    </div>
  );
};
